import { Box } from "@rebass/grid";
import createRentDepositForm from "@rentiohq/shared-frontend/dist/forms/createRentDeposit";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "@rentiohq/shared-frontend/dist/forms/createRentDeposit/schema.createRentDeposit.actions";
import { EField } from "@rentiohq/shared-frontend/dist/forms/createRentDeposit/schema.createRentDeposit.types";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { EContactCustomId } from "@rentiohq/shared-frontend/dist/redux/contact/contact.types";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import { EDocumentCustomFilter } from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.types";
import * as documentUtils from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.utils";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { EContractMemberTypes } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EDocumentCategory } from "@rentiohq/shared-frontend/dist/types/document.types";
import { getMembersWithOneOfRoles } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  Card,
  DisplayText,
  Error,
  ESpacings,
  List,
  Loading,
  MultiStepForm,
  Page,
  SuccessModal,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import { uniq } from "lodash";
import React, { FC, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { ConfirmContactDetailsModal } from "scenes/Properties/modals/ConfirmContactDetailsModal";
import { ts } from "../../services";

const formId = generateFormId();

export const RentDepositForm: FC<{}> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { internalModeEnabled } = useInternalMode();
  const hasBanqupEnabled = brokerHooks.useBrokerFeature(
    EBrokerFeature.EnableBanqup,
  );

  const { contractId, ...params } = useParams<{
    propertyId: string;
    contractId: string;
  }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  const [formData, setFormData] = useState();
  const [createdRentDepositId, setCreatedRentDepositId] = useState<number>();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [completionAction, setCompletionAction] = useState(() => () => {});
  const [contactIdToConfirm, setContactIdToConfirm] = useState<
    number | undefined
  >();
  const [showConfirmContactDetailsModal, setShowConfirmContactDetailsModal] =
    useState(false);

  const { isBroker } = authHooks.useSelf();
  const contactMeMaster = useSelector((state: IRootStore) =>
    contactSelectors.getContactMeMaster(state),
  );

  const legalContact = useSelector((state: IRootStore) =>
    isBroker
      ? contactSelectors.getContactByCustomId(state, EContactCustomId.Legal)
      : undefined,
  );

  const isFetchingLegalContact = useSelector((state: IRootStore) =>
    isBroker
      ? state.contact.contactsById[EContactCustomId.Legal].isFetching
      : false,
  );

  // const hasInsurance = useBrokerFeature(EBrokerFeature.Insurance);

  const { detail: contract, fetchError: contractFetchError } =
    contractHooks.useDetail({
      shouldRefetch: false,
      id: contractId,
    });

  const { detail: property, fetchError: propertyFetchError } =
    propertyHooks.useDetail({
      shouldRefetch: false,
      id: propertyId,
    });

  // Account ids of everyone selectable as a signer on this contract, mirroring
  // the option list of the signers field. An ID card uploaded by a signer is
  // linked to their account rather than to the property, so it has to be matched
  // on `accountId` instead of `propertyId`.
  const signerAccountIds = React.useMemo(() => {
    if (!contract) {
      return [];
    }

    return uniq(
      getMembersWithOneOfRoles(contract.members, [
        EContractMemberTypes.Owner,
        EContractMemberTypes.Tenant,
        EContractMemberTypes.Parent,
      ]).map(member => member.account.id),
    );
  }, [contract]);

  // Tenant ID-card documents (front/back) used to pre-fill the identity document
  // fields on the "Identification document" step: the ones stored on the
  // property, plus the ones stored on a signer's own account.
  // `items` is undefined until the fetch resolves ([] once fetched but empty),
  // which lets us hold the form until the documents are available so the schema
  // defaults are computed with them (avoids an intermittent pre-fill race).
  // The query is only built once the contract is loaded, so the signer accounts
  // are part of the very first fetch. `usePaged` skips an undefined query.
  const { items: tenantIdDocuments, fetchError: tenantIdDocumentsFetchError } =
    documentHooks.usePaged(
      contract
        ? documentUtils.getDocumentPagedQuery({
            extraFilterData: {
              categoryId: {
                in: [
                  EDocumentCategory.IdCardFront,
                  EDocumentCategory.IdCardBack,
                ],
              },
              ...(signerAccountIds.length > 0
                ? {
                    or: [
                      { propertyId: { eq: propertyId } },
                      { accountId: { in: signerAccountIds } },
                    ],
                  }
                : { propertyId: { eq: propertyId } }),
            },
            customFilters: [EDocumentCustomFilter.Active],
          })
        : { query: undefined, shouldRefetch: true },
    );

  const isTenantIdDocumentsReady =
    tenantIdDocuments !== undefined || !!tenantIdDocumentsFetchError;

  React.useEffect(() => {
    if (!contract) {
      return;
    }

    const ownerIds = contract.members
      .filter(member =>
        member.roles.some(role => role === EContractMemberTypes.Owner),
      )
      .map((member: any) => member.account.id);

    dispatch(
      contactActions.getContactsByAccountIds.actions.start({
        accountIds: ownerIds,
      }),
    );
  }, [contract]);

  const fetchError = contractFetchError || propertyFetchError;

  if (isBroker && isFetchingLegalContact) {
    return <Loading />;
  }

  if (fetchError) {
    return <Error errors={[fetchError]} />;
  }

  const handleConfirmContactDetails = (
    formData: any,
    onSuccess: () => void,
  ) => {
    const payerIdentityDocuments =
      formData[EField.PayerIdentityDocuments] || [];
    const payerId = payerIdentityDocuments[0]?.payerAccountId;

    setFormData(formData);
    setContactIdToConfirm(payerId);
    setShowConfirmContactDetailsModal(true);
    setCompletionAction(() => onSuccess);
  };

  const handleCloseConfirmContactDetailsModal = () => {
    setContactIdToConfirm(undefined);
    setShowConfirmContactDetailsModal(false);
  };

  const handleSuccess = (rentDepositId: number) => {
    setCreatedRentDepositId(rentDepositId);

    setModalOpen(true);
  };

  const handleModalClose = () => {
    handleFinish();
  };

  const handleFinish = () => {
    navigate(
      `/properties/${propertyId}?rentDepositId=${createdRentDepositId}&requestInsuranceContractId=${contractId}`,
    );
  };

  const renderForm = () => {
    if (!property) {
      return null;
    }

    if (!contract) {
      return null;
    }

    // Wait for the tenant ID documents so the identity-document fields are
    // pre-filled deterministically on the first render of the step.
    if (!isTenantIdDocumentsReady) {
      return <Loading />;
    }

    const extraData = {
      property,
      contract,
      isBroker,
      legalContact,
      contactMeMaster,
      withInsuranceRequest: true,
      showProviderOptions: internalModeEnabled && hasBanqupEnabled,
      hasBanqupEnabled,
      tenantIdDocuments: tenantIdDocuments ?? [],
      preSubmitAction: handleConfirmContactDetails,
    };

    return (
      <MultiStepForm
        formId={`rent-deposit-form-${formId}`}
        schemas={createRentDepositForm(extraData)}
        withSummary={true}
        submitLabel={ts.system("save_send")}
        isPerformingSubmitActionSelector={isPerformingSubmitActionSelector()}
        performingSubmitActionResultSelector={performingSubmitActionResultSelector()}
        performingSubmitActionErrorSelector={performingSubmitActionErrorSelector()}
        submitActionCreator={(formData: any) =>
          submitActionCreator(extraData)(formData)
        }
        onSuccess={handleSuccess}
      />
    );
  };

  const modalActions = [
    {
      content: ts.system("close"),
      onClick: handleModalClose,
      appearance: "primary",
    } as any,
  ];

  return (
    <Page
      title={ts.rentDepositCreateHeading()}
      // subtitle={formatAddress(property)}
      breadcrumbs={{
        to: !!propertyId
          ? `/properties/${propertyId}`
          : "/follow-up/rent-deposits?page=1",
        content: !!propertyId
          ? ts.contractDetailBreadcrumb()
          : ts.followUpRentDepositsBreadcrumb(),
      }}
    >
      {!isModalOpen && <Card>{renderForm()}</Card>}
      {isModalOpen && (
        <SuccessModal onClose={handleModalClose} actions={modalActions}>
          <DisplayText>
            {ts.contractOpenRentDepositAddedModalHeading()}
          </DisplayText>
          <Box mb={ESpacings.base}>
            {ts.contractOpenRentDepositAddedModalIntro()}
          </Box>
          <Box mb={ESpacings.base}>
            <DisplayText size="medium">
              {ts.contractOpenRentDepositAddedModalNextStepsHeading()}
            </DisplayText>
            <List>
              <List.Item>
                {ts.contractOpenRentDepositAddedModalNextStepsStep(1)}
              </List.Item>
              <List.Item>
                {ts.contractOpenRentDepositAddedModalNextStepsStep(2)}
              </List.Item>
              <List.Item>
                {ts.contractOpenRentDepositAddedModalNextStepsStep(3)}
              </List.Item>
            </List>
          </Box>
          <Box>{ts.contractOpenRentDepositAddedModalOutro()}</Box>
        </SuccessModal>
      )}
      {showConfirmContactDetailsModal && contactIdToConfirm && (
        <ConfirmContactDetailsModal
          formData={formData}
          accountId={contactIdToConfirm}
          onClose={handleCloseConfirmContactDetailsModal}
          onSave={() => completionAction()}
        />
      )}
    </Page>
  );
};
